import requestIdleCallback from '../shims/requestIdleCallback';
import { updateCounterBadge } from './alerts';

type SavedItem = {
    id: number;
    property: SavedItemType;
    value: string;
    contacted: boolean;
    discarded: boolean;
}

enum SavedItemType {
    Property        = 'property',
    PropertySearch  = 'property-search',
    Directory       = 'directory',
    DircetorySearch = 'directory-search',
    Article         = 'information'
}

export enum SaveType {
    Item    = 'item',
    Search  = 'search',
}

export type UpdateSavedCounterEvent = {
    delta: number;
    type: SaveType;
}

function updateSavedCounter( event: CustomEvent<UpdateSavedCounterEvent> ): void {
    const detail = event.detail || {};

    if ( !detail.delta ) {
        return;
    }

    const delta = detail.delta;

    let selector: string;

    if ( detail.type === SaveType.Search ) {
        selector = '.saved-search-counter';
    } else {
        selector = '.saved-item-counter';
    }

    const counters = document.querySelectorAll<HTMLElement>( selector );

    if ( !counters ) {
        return;
    }

    let currentValue = parseInt( counters[ 0 ].innerHTML );

    if ( isNaN( currentValue ) ) {
        currentValue = 0;
    }

    counters.forEach( ( counter: HTMLElement ) => updateCounterBadge( counter, currentValue + delta ) );
}

function persistUserSavedInSessionStorage( savedItems: Array<SavedItem> ) {
    const storageObjects = {
        savedProperties: {},
    };

    if ( !savedItems ) {
        Object.keys( storageObjects ).forEach( function( key ) {
            window.appStorage.setSessionItem( key, JSON.stringify( storageObjects[ key ] ) );
        } );
        return;
    }

    savedItems
        .filter( function( item: SavedItem ) {
            return item.property === SavedItemType.Property;
        } )
        .forEach( function( savedProperty: SavedItem ) {
            storageObjects.savedProperties[ parseInt( savedProperty.value ) ] = savedProperty;
        } );

    Object.keys( storageObjects ).forEach( function( key ) {
        window.appStorage.setSessionItem( key, JSON.stringify( storageObjects[ key ] ) );
    } );
}

function onUpdateSavedStatus( event: CustomEvent ): void {
    const detail = event.detail || {};
    requestIdleCallback( () => {
        updateSavedStatus( detail.container || document.body );
    } );
}

type ListSavedResponse = {
    saved: Array<SavedItem>;
    success: boolean;
}

async function updateSavedStatus( container: HTMLElement ): Promise<void> {
    if ( !container ) {
        return;
    }

    const data = await window.http( new URL( window.location.origin + '/saved/list' ) );

    if ( !data.ok ) {
        throw new Error( 'failed getting users saved list with status ' + data.status );
    }

    const response = await data.json() as ListSavedResponse;

    persistUserSavedInSessionStorage( response.saved );

    document.dispatchEvent( new CustomEvent( 'twc:saved-list-updated', {
        bubbles: true,
        detail: {
            items: response.saved,
        },
    } ) );

    let saved = response.saved || [];

    saved = saved.filter( ( savedItem: SavedItem ) => !savedItem.discarded );

    const savedItems = saved.filter( ( item ) => !/-search$/.test( item.property ) );

    document.querySelectorAll<HTMLElement>( '.saved-item-counter' ).forEach( ( counter: HTMLElement ) => updateCounterBadge( counter, savedItems.length ) );

    const savedSearches = saved.filter( ( item ) => /-search$/.test( item.property ) );

    document.querySelectorAll<HTMLElement>( '.saved-search-counter' ).forEach( ( counter: HTMLElement ) => updateCounterBadge( counter, savedSearches.length ) );

    const els = container.querySelectorAll<HTMLElement>( '.twc__savable, [data-saved-id]' );

    if ( els.length === 0 ) {
        return;
    }

    const saveableElements = container.querySelectorAll( '[data-saveable]' );

    for ( let saveableIndex = 0; saveableIndex < saveableElements.length; saveableIndex++ ) {
        const saveableElement = saveableElements[ saveableIndex ] as HTMLElement;

        const matchingSaved = saved.filter( function( s ) {
            return saveableElement.dataset.saveValue === s.value && saveableElement.dataset.saveProperty === s.property;
        } );

        if ( saveableElement.dataset.handler && saveableElement.dataset.handler.length > 0 ) {
            const handlerSplit = saveableElement.dataset.handler.split( ':' );

            const handler = {
                action: handlerSplit[ 0 ],
                type: handlerSplit[ 1 ],
                name: handlerSplit[ 2 ],
            };

            let required;

            if ( handler.action === 'save' && matchingSaved.length === 0 ) {
                required = true;
            } else if ( handler.action === 'save' && matchingSaved.length > 0 ) {
                required = false;
            }

            saveableElement.dataset.requiresHandler = required;
        }

        const replacable = saveableElement.querySelector( '.text' ) || saveableElement;

        if ( matchingSaved.length === 0 ) {
            replacable.innerHTML = saveableElement.dataset.saveSave || '';
            saveableElement.classList.remove( 'is-not-contacted' );
            saveableElement.classList.remove( 'favourite-alt' );
            saveableElement.classList.remove( 'favourite' );
            continue;
        }

        const foundSaved = matchingSaved[ 0 ];

        if ( foundSaved.contacted ) {
            saveableElement.innerHTML = saveableElement.dataset.saveContacted || '';
            saveableElement.classList.add( 'contacted' );
        } else {
            replacable.innerHTML = saveableElement.dataset.saveSaved || '';
            saveableElement.classList.remove( 'contacted' );

            if ( saveableElement.classList.contains( 'favourite-button' ) ) {
                saveableElement.classList.add( 'favourite' );
            } else {
                saveableElement.classList.add( 'favourite-alt' );
            }
        }
    }

    // now checking all the data-saved-id element for /user/saved page
    const saveIdsElements = container.querySelectorAll<HTMLElement>( '[data-saved-id]' );

    for ( let saveIdsIndex = 0; saveIdsIndex < saveIdsElements.length; saveIdsIndex++ ) {
        const saveIdElement = saveIdsElements[ saveIdsIndex ] as HTMLElement;

        const matchingUserSaved = saved.filter( function( s ) {
            return parseInt( saveIdElement.dataset.savedId || '' ) === s.id;
        } );

        if ( matchingUserSaved.length === 0 ) {
            continue;
        }

        const foundUserSaved = matchingUserSaved[ 0 ];

        if ( foundUserSaved.contacted ) {
            saveIdElement.classList.add( 'is-contacted' );
            saveIdElement.classList.remove( 'is-not-contacted' );
        } else {
            saveIdElement.classList.remove( 'is-contacted' );
            saveIdElement.classList.add( 'is-not-contacted' );
        }
    }
}

export {
    updateSavedStatus,
    updateSavedCounter,
    onUpdateSavedStatus,
};

